import React, { useState, useRef } from "react";
import { ChatTabsDesktop } from "./ChatTabsDesktop";
import { ChatTabsMobile } from "./ChatTabsMobile";
import { NotebookPen, Copy, FileDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNotesEditor } from "@/context/NotesEditorContext";
import { getNotesAsText, exportNotesAsPDF } from "@/lib/notes/exportNotes";
import { ChatPanel } from "./panels/ChatPanel";
import { FlashcardsPanel } from "./panels/FlashcardsPanel";
import { QuizzesPanel } from "./panels/QuizzesPanel";
import { NotesPanel } from "./panels/NotesPanel";
import { ChaptersPanel } from "./panels/ChaptersPanel";
import { SummaryPanel } from "./panels/SummaryPanel";
import { NotesFloater } from "@/components/notes/NotesFloater";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { QuizHelperRequest } from "@/types/quizHelpers";
import { buildQuizHelperUserMessage } from "@/lib/quizHelperPrompts";

export type ChatTabId = "chat" | "flashcards" | "quizzes" | "notes" | "chapters" | "summary";

interface ChatRootProps {
  initialMessages?: any[];
  chatId?: string;
  allowedTabs?: ChatTabId[];
}

export const ChatRoot: React.FC<ChatRootProps> = ({
  initialMessages = [],
  chatId,
  allowedTabs
}) => {
  const [activeTab, setActiveTab] = useState<ChatTabId>(allowedTabs?.[0] || "chat");
  const [isFloaterOpen, setIsFloaterOpen] = useState(false);
  const [pendingHelperMessage, setPendingHelperMessage] = useState<string | null>(null);
  const { toast } = useToast();
  const { editor } = useNotesEditor();

  const handleCopyNotes = async () => {
    if (!editor) {
      toast({
        title: "Error",
        description: "Notes editor not ready",
        variant: "destructive"
      });
      return;
    }
    try {
      const text = getNotesAsText(editor);
      if (!text.trim()) {
        toast({
          title: "No content",
          description: "There are no notes to copy",
          variant: "destructive"
        });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: "Notes copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy notes",
        variant: "destructive"
      });
    }
  };

  const handleExportPDF = async () => {
    if (!editor) {
      toast({
        title: "Error",
        description: "Notes editor not ready",
        variant: "destructive"
      });
      return;
    }
    try {
      await exportNotesAsPDF(editor);
      toast({
        title: "Success!",
        description: "Notes exported as PDF"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to export PDF",
        variant: "destructive"
      });
    }
  };

  const handleOpenFloater = () => {
    setIsFloaterOpen(true);
  };

  // Handle quiz helper requests - switch to chat and send the message
  const handleQuizHelperRequest = (req: QuizHelperRequest) => {
    const message = buildQuizHelperUserMessage(req);
    setPendingHelperMessage(message);
    setActiveTab("chat");
  };

  // Clear pending message after it's consumed by ChatPanel
  const handlePendingMessageConsumed = () => {
    setPendingHelperMessage(null);
  };

  // Show Notes Floater button only on notes tab
  const showFloaterButton = activeTab === "notes";

  return (
    <div className="flex h-full w-full flex-col">
      {/* Notes Floater */}
      <NotesFloater
        isOpen={isFloaterOpen}
        onClose={() => setIsFloaterOpen(false)}
        title="Notes"
      />

      {/* Desktop */}
      <div className="hidden h-full w-full md:flex md:flex-col overflow-hidden">
        <div className="flex-shrink-0 flex justify-center pt-4">
          <ChatTabsDesktop activeTab={activeTab} setActiveTab={setActiveTab} allowedTabs={allowedTabs} className="mx-0 px-[6px]" />
        </div>

        {/* Header controls */}
        <div className="flex-shrink-0 flex justify-end px-6 pt-2">
          <TooltipProvider>
            <div className="flex items-center justify-end gap-2">
              {showFloaterButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleOpenFloater}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-auto border-none bg-background/80 px-2 py-1 text-sm text-muted-foreground backdrop-blur-sm"
                    >
                      <span className="flex items-center gap-1">
                        <NotebookPen className="h-4 w-4" />
                        <span>Notes Floater</span>
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Open Notes Floater</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {activeTab === "notes" && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleCopyNotes}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy Notes</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleExportPDF}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <FileDown className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Download Notes</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </TooltipProvider>
        </div>

        <div className="mt-2 flex-1 min-h-0 overflow-hidden">
          {activeTab === "chat" && (
            <ChatPanel 
              initialMessages={initialMessages} 
              chatId={chatId} 
              onSwitchTab={setActiveTab}
              pendingMessage={pendingHelperMessage}
              onPendingMessageConsumed={handlePendingMessageConsumed}
            />
          )}
          {activeTab === "flashcards" && <FlashcardsPanel chatId={chatId} />}
          {activeTab === "quizzes" && <QuizzesPanel onHelperRequest={handleQuizHelperRequest} />}
          {activeTab === "chapters" && <ChaptersPanel />}
          {activeTab === "summary" && <SummaryPanel />}
          {/* Always render NotesPanel so editor is available for "Add to Notes" from any tab */}
          <div className={activeTab === "notes" ? "h-full" : "hidden"}>
            <NotesPanel />
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="flex h-[calc(100dvh-70px)] flex-col md:hidden">
        <div className="px-2 pt-2">
          <ChatTabsMobile activeTab={activeTab} setActiveTab={setActiveTab} allowedTabs={allowedTabs} />
        </div>

        {/* Header controls */}
        <div className="flex justify-end px-4 pt-2">
          <TooltipProvider>
            <div className="sticky top-0 z-20 flex items-center justify-end gap-2">
              {showFloaterButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleOpenFloater}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-auto border-none bg-background/80 px-2 py-1 text-sm text-muted-foreground backdrop-blur-sm"
                    >
                      <span className="flex items-center gap-1">
                        <NotebookPen className="h-4 w-4" />
                        <span>Notes Floater</span>
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Open Notes Floater</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {activeTab === "notes" && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleCopyNotes}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy Notes</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleExportPDF}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <FileDown className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Download Notes</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </TooltipProvider>
        </div>

        <div className="mt-2 flex-1 overflow-hidden">
          {activeTab === "chat" && (
            <ChatPanel 
              isMobile 
              initialMessages={initialMessages} 
              chatId={chatId} 
              onSwitchTab={setActiveTab}
              pendingMessage={pendingHelperMessage}
              onPendingMessageConsumed={handlePendingMessageConsumed}
            />
          )}
          {activeTab === "flashcards" && <FlashcardsPanel isMobile chatId={chatId} />}
          {activeTab === "quizzes" && <QuizzesPanel isMobile onHelperRequest={handleQuizHelperRequest} />}
          {activeTab === "chapters" && <ChaptersPanel isMobile />}
          {activeTab === "summary" && <SummaryPanel isMobile />}
          {/* Always render NotesPanel so editor is available for "Add to Notes" from any tab */}
          <div className={activeTab === "notes" ? "h-full" : "hidden"}>
            <NotesPanel isMobile />
          </div>
        </div>
      </div>
    </div>
  );
};
